/**
 * API client adapter.
 *
 * Wraps fetch with consistent error handling, headers, and observability.
 */

import { config } from "./config"
import { obs } from "./obs"

// Token getter function - set by AuthProvider
let _getAccessToken: (() => Promise<string | null>) | null = null

// View-as user ID - set by admin impersonation
let _viewAsUserId: string | null = null

/**
 * Set the token getter function.
 * Called by AuthProvider to inject token retrieval.
 */
export function setTokenGetter(getter: () => Promise<string | null>) {
  _getAccessToken = getter
}

/**
 * Set the user ID to impersonate (admin only).
 * Pass null to stop impersonating.
 */
export function setViewAsUser(userId: string | null) {
  _viewAsUserId = userId
}

/**
 * Get the current view-as user ID (if set).
 */
export function getViewAsUserId(): string | null {
  return _viewAsUserId
}

/**
 * Get the current access token (if available).
 */
export async function getAccessToken(): Promise<string | null> {
  if (!_getAccessToken) return null
  return _getAccessToken()
}

export class ApiError extends Error {
  status: number
  body?: unknown

  constructor(message: string, status: number, body?: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.body = body
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
  body?: unknown
  headers?: Record<string, string>
  /** Skip JSON parsing of response */
  raw?: boolean
}

/**
 * Make an API request with consistent error handling.
 *
 * @param path - API path (without base URL)
 * @param options - Request options
 * @returns Parsed JSON response
 */
export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = "GET", body, headers = {}, raw = false } = options
  const url = `${config.apiBase}${path}`

  const requestHeaders: Record<string, string> = {
    ...headers,
  }

  // Add auth token if available
  const token = await getAccessToken()
  if (token) {
    requestHeaders["Authorization"] = `Bearer ${token}`
  }

  // Add view-as header for admin impersonation
  if (_viewAsUserId) {
    requestHeaders["X-View-As-User"] = _viewAsUserId
  }

  if (body && !requestHeaders["Content-Type"]) {
    requestHeaders["Content-Type"] = "application/json"
  }

  obs.debug(`API ${method} ${path}`, { url, authenticated: !!token })

  try {
    const response = await fetch(url, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
    })

    if (!response.ok) {
      const errorText = await response.text()
      obs.error(`API error: ${method} ${path}`, undefined, {
        status: response.status,
        body: errorText,
      })
      throw new ApiError(
        errorText || `Request failed with status ${response.status}`,
        response.status,
        errorText
      )
    }

    if (raw) {
      return response as unknown as T
    }

    const data = await response.json()
    obs.debug(`API response: ${method} ${path}`, { status: response.status })
    return data as T
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }
    obs.error(`API request failed: ${method} ${path}`, error)
    throw new ApiError(
      error instanceof Error ? error.message : "Network error",
      0,
      error
    )
  }
}

/**
 * Upload a file via multipart form data.
 *
 * @param path - API path
 * @param file - File to upload
 * @param fieldName - Form field name (default: "file")
 * @returns Parsed JSON response
 */
export async function uploadFile<T = unknown>(
  path: string,
  file: File,
  fieldName = "file"
): Promise<T> {
  const url = `${config.apiBase}${path}`
  const formData = new FormData()
  formData.append(fieldName, file)

  // Add auth token if available
  const token = await getAccessToken()
  const headers: Record<string, string> = {}
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  // Add view-as header for admin impersonation
  if (_viewAsUserId) {
    headers["X-View-As-User"] = _viewAsUserId
  }

  obs.debug(`API upload ${path}`, { filename: file.name, size: file.size, authenticated: !!token })

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  })

  if (!response.ok) {
    const errorText = await response.text()
    obs.error(`API upload error: ${path}`, undefined, {
      status: response.status,
      body: errorText,
    })
    throw new ApiError(
      errorText || `Upload failed with status ${response.status}`,
      response.status,
      errorText
    )
  }

  return response.json() as Promise<T>
}
